/**
 * Simple Flight Service
 * 
 * This service only reads from DynamoDB - no direct FlightAware API calls.
 * The backend handles all FlightAware integration and updates DynamoDB.
 * Frontend just polls DynamoDB every 5 minutes.
 */

import { API_BASE_URL } from '../config/environment';
import { authFetch } from '../utils/authFetch';

class SimpleFlightService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get trip data with flight statuses from DynamoDB
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @returns {Promise<Object>} Trip data with flight statuses
   */
  async getTripWithFlightStatuses(userId, tripId) {
    try {
      const response = await authFetch(`${this.baseURL}/trips/${tripId}?userId=${userId}&includeFlightStatuses=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch trip: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Extract trip data from the API response
      const tripData = responseData.trip || responseData;

      return tripData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all trips for a user with flight statuses
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of trips with flight statuses
   */
  async getAllTripsWithFlightStatuses(userId) {
    try {
      const response = await authFetch(`${this.baseURL}/trips?userId=${userId}&includeFlightStatuses=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch trips: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Extract trips array from the API response
      const trips = responseData.trips || responseData;

      return trips;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get flight status from DynamoDB
   * @param {Object} trip - Trip object with flightStatuses
   * @param {string} flightId - Flight ID
   * @returns {Object|null} Flight status from DynamoDB
   */
  getFlightStatus(trip, flightId) {
    if (!trip?.flightStatuses) {
      return null;
    }
    
    const status = trip.flightStatuses[flightId];
    if (status) {
      // Parse the JSON string if it's a string
      if (typeof status === 'string') {
        try {
          const parsedStatus = JSON.parse(status);
          return parsedStatus;
        } catch (error) {
          return status; // Return original if parsing fails
        }
      }
    }
    
    return status || null;
  }

  /**
   * Check if flight statuses are recent
   * @param {Object} trip - Trip object
   * @returns {boolean} True if statuses are recent
   */
  isStatusRecent(trip) {
    if (!trip?.flightStatusesLastChecked) {
      return false;
    }
    
    const lastChecked = new Date(trip.flightStatusesLastChecked);
    const now = new Date();
    const ageMinutes = (now - lastChecked) / (1000 * 60);
    
    // Status is recent if checked within last 10 minutes
    return ageMinutes < 10;
  }

  /**
   * Get status freshness info for debugging
   * @param {Object} trip - Trip object
   * @returns {Object} Status freshness information
   */
  getStatusInfo(trip) {
    const hasStatuses = !!trip?.flightStatuses;
    const isRecent = this.isStatusRecent(trip);
    const lastChecked = trip?.flightStatusesLastChecked;
    const statusCount = Object.keys(trip?.flightStatuses || {}).length;
    
    return {
      hasStatuses,
      isRecent,
      lastChecked,
      statusCount,
      ageMinutes: lastChecked ? Math.round((Date.now() - new Date(lastChecked).getTime()) / (1000 * 60)) : null
    };
  }
}

// Export singleton instance
export default new SimpleFlightService();
